// MultiSelectDemographicFilter.tsx
import { Box, Checkbox, Flex, Input, Stack, VStack } from '@chakra-ui/react';
import React, { useCallback, useRef, useState } from 'react';
import { HelivanText } from '../helivan/HelivanText';

// Define types for the component
interface MultiSelectDemographicFilterProps {
  selectedDemographicIds: Set<string>; // Stored as "key-value" strings
  setSelectedDemographicIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  allDemographics: Record<string, string[]>; // Categories and their values
  maxSelections?: number;
  onClose: () => void;
}

const MultiSelectDemographicFilter: React.FC<
  MultiSelectDemographicFilterProps
> = ({
  selectedDemographicIds,
  setSelectedDemographicIds,
  allDemographics,
  maxSelections = 6,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Helper to check if a demographic is selected
  const isDemographicSelected = useCallback(
    (key: string, value: string): boolean => {
      return selectedDemographicIds.has(`${key}-${value}`);
    },
    [selectedDemographicIds]
  );

  // Handle demographic selection/deselection
  const handleDemographicToggle = useCallback(
    (key: string, value: string) => {
      const demographicId = `${key}-${value}`;

      setSelectedDemographicIds((prev) => {
        // Create a new Set to ensure state update
        const updated = new Set<string>(Array.from(prev));

        if (isDemographicSelected(key, value)) {
          // If already selected, remove it
          updated.delete(demographicId);
        } else {
          // Remove any existing selection for this key
          Array.from(updated).forEach((id) => {
            if (id.startsWith(`${key}-`)) {
              updated.delete(id);
            }
          });

          // Only add if we haven't reached max selections
          if (updated.size < maxSelections) {
            updated.add(demographicId);
          }
        }

        return updated;
      });
    },
    [isDemographicSelected, maxSelections, setSelectedDemographicIds]
  );

  // Filter demographics based on search term
  const filteredDemographics = Object.entries(allDemographics).reduce(
    (acc, [key, values]) => {
      const filteredValues = values.filter((value) =>
        value.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (filteredValues.length > 0) {
        acc[key] = filteredValues;
      }

      return acc;
    },
    {} as Record<string, string[]>
  );

  const menuRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Click was outside the menu
        if (onClose) {
          onClose();
        }
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <Box
      ref={menuRef}
      position="fixed"
      width="250px"
      boxShadow="md"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      p={2}
      zIndex={10}
    >
      <Stack spacing={3}>
        {/* Search input */}
        <Input
          placeholder="Search demographics..."
          size="sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* Demographics list - FIXED SCROLLING CONTAINER */}
        <Box maxH="200px" overflowY="auto">
          {Object.entries(filteredDemographics).length === 0 ? (
            <HelivanText variant="smallBody">No demographics found</HelivanText>
          ) : (
            <VStack align="stretch" spacing={3}>
              {Object.entries(filteredDemographics).map(([key, values]) => (
                <Stack key={key} spacing={1}>
                  <HelivanText variant="smallBody+">{key}</HelivanText>
                  {values.map((value) => (
                    <Flex
                      key={`${key}-${value}`}
                      width="full"
                      justifyContent="space-between"
                      _hover={{ cursor: 'pointer', bg: '#EBF6FF' }}
                      onClick={() => handleDemographicToggle(key, value)}
                      p="6px"
                      borderRadius="6px"
                      bg={
                        isDemographicSelected(key, value) ? '#DAEFFF' : 'white'
                      }
                    >
                      <HelivanText variant="largeBody" colorOverride="black">
                        {value}
                      </HelivanText>
                      <Checkbox
                        isChecked={isDemographicSelected(key, value)}
                        isDisabled={
                          !isDemographicSelected(key, value) &&
                          // Disable if we've reached max selections
                          (selectedDemographicIds.size >= maxSelections ||
                            // Or if another value in this category is already selected (but not this one)
                            Array.from(selectedDemographicIds).some(
                              (id) =>
                                id.startsWith(`${key}-`) &&
                                id !== `${key}-${value}`
                            ))
                        }
                      />
                    </Flex>
                  ))}
                </Stack>
              ))}
            </VStack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default React.memo(MultiSelectDemographicFilter);
